import React, { useEffect, useRef, useState } from 'react'

import { useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import { getCurrentOperatorActorId } from '../../utils/operatorSession'
import { createId } from '../../utils/id'
import { mortalityLogApi, type MortalityLogMutationResult } from '../../utils/mortalityLogApi'
import type { MortalityLog } from '../../types/production'
import OperatorGate from './OperatorGate'
import CrewModeFarmBar from './CrewModeFarmBar'
import { useCrewModeFarm } from './useCrewModeFarm'
import { isMortalityCrewModeEnabled } from './mortalityLogFeatureFlag'
import { recordCrewEvent } from './crewModeTelemetry'
import { ensureTodayProductionRow } from './ensureTodayProductionRow'
import { useCrewLocation } from './useCrewLocation'
import { CrewLocationHeader, CrewLocationPicker, CrewNextLocationButton } from './CrewLocationPicker'
import { buildOperatorUndoAudit, recordedAtForEntryDay, type CrewEntryDay } from './crewCaptureHelpers'
import {
  GROW_OUT_CAUSE_CODES,
  HATCHERY_CAUSE_CODES,
  HATCHERY_COUNT_LARGE_WARNING_THRESHOLD,
  NOT_CHECKED_REASON_CODES,
  buildMortalityVisitPayloads,
  findLikelyDuplicateMortalityLog,
  isUnusuallyLargeMortalityCount,
  validateMortalityCountValue,
  type MortalityCauseCode,
  type MortalityVisit,
  type NotCheckedReasonCode
} from './mortalityCaptureValidation'
import { farmDayKey, farmTimeZone } from '../../utils/farmTime'
import { isUnsyncedBadgeState, useRecheckWhileUnsynced } from './useRecheckWhileUnsynced'

// Mortality v0 PRD, extended by Crew Mode Field Bundle v0 §3 item 8:
// - shared location header ("Lot · Unidad"), "Siguiente" after Confirmar;
// - "Sin mortalidad hoy" in one tap (explicit zero, findings #74);
// - several causes in one visit, one MortalityLog per cause sharing a visit id;
// - optional note; "Hoy / Ayer / Hace 2 días" with a late-entry mark;
// - optional "No se pudo revisar" with a reason (ASC 4.1.1.10.4);
// - undo of the operator's own last visit (audited delete).
// Charter O-1/O-3: no rate, cumulative mortality, survival % or population is
// shown or computed; the large-count warning uses a fixed threshold. D-1/D-3:
// the location is a lot segment (lot + its unit); the server re-runs I-8 C3
// at recordedAt (late entries included). D-4: every row carries the operator.

type SyncBadgeState = 'local' | 'pending' | 'synced' | 'error'

const chipButtonStyle: React.CSSProperties = {
  minHeight: 48,
  borderRadius: 12,
  border: '1px solid #d1d5db',
  backgroundColor: 'white',
  fontWeight: 800,
  fontSize: 14,
  cursor: 'pointer',
  padding: '8px 12px'
}

const primaryButtonStyle: React.CSSProperties = {
  minHeight: 56,
  borderRadius: 12,
  border: '1px solid #1d4ed8',
  backgroundColor: '#1d4ed8',
  color: 'white',
  fontWeight: 800,
  fontSize: 16,
  cursor: 'pointer',
  width: '100%'
}

const zeroButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  backgroundColor: '#166534',
  borderColor: '#166534'
}

const secondaryButtonStyle: React.CSSProperties = {
  minHeight: 48,
  borderRadius: 12,
  border: '1px solid #d1d5db',
  backgroundColor: 'white',
  color: '#374151',
  fontWeight: 800,
  fontSize: 14,
  cursor: 'pointer',
  width: '100%'
}

const syncBadgeStyle: Record<SyncBadgeState, React.CSSProperties> = {
  local: { backgroundColor: '#f3f4f6', color: '#374151' },
  pending: { backgroundColor: '#fffbeb', color: '#92400e' },
  synced: { backgroundColor: '#f0fdf4', color: '#166534' },
  error: { backgroundColor: '#fef2f2', color: '#991b1b' }
}

const syncBadgeLabelKey: Record<SyncBadgeState, TranslationKey> = {
  local: 'crewMode.mortality.sync.local',
  pending: 'crewMode.mortality.sync.pending',
  synced: 'crewMode.mortality.sync.synced',
  error: 'crewMode.mortality.sync.error'
}

const causeLabelKey: Record<MortalityCauseCode, TranslationKey> = {
  predation: 'crewMode.mortality.cause.predation',
  disease: 'crewMode.mortality.cause.disease',
  handling: 'crewMode.mortality.cause.handling',
  water_quality: 'crewMode.mortality.cause.waterQuality',
  unknown: 'crewMode.mortality.cause.unknown',
  dead_egg: 'crewMode.mortality.cause.deadEgg',
  not_hatched: 'crewMode.mortality.cause.notHatched',
  deformed: 'crewMode.mortality.cause.deformed',
  fungus: 'crewMode.mortality.cause.fungus'
}

const notCheckedLabelKey: Record<NotCheckedReasonCode, TranslationKey> = {
  weather: 'crewMode.mortality.notChecked.weather',
  equipment: 'crewMode.mortality.notChecked.equipment',
  no_staff: 'crewMode.mortality.notChecked.noStaff',
  other: 'crewMode.mortality.notChecked.other'
}

const DAY_OPTIONS: Array<{ value: CrewEntryDay; labelKey: TranslationKey }> = [
  { value: 0, labelKey: 'crewMode.day.today' },
  { value: 1, labelKey: 'crewMode.day.yesterday' },
  { value: 2, labelKey: 'crewMode.day.twoDaysAgo' }
]

const toSyncBadgeState = (syncState: MortalityLogMutationResult['syncState']): SyncBadgeState => (
  syncState === 'synced' ? 'synced' : syncState === 'pending' ? 'pending' : 'local'
)

type CauseRow = { id: string; causeCode: MortalityCauseCode | null; count: string }

type LastVisitStatus = {
  mortalityLogIds: string[]
  summary: string
  locationLabel: string
  lateEntry: boolean
  state: SyncBadgeState
}

const newRow = (): CauseRow => ({ id: createId('cause'), causeCode: null, count: '' })

export default function MortalityCapture() {
  const t = useT()
  const { farms, farmId, selectFarm } = useCrewModeFarm()
  const crewLocation = useCrewLocation({ stream: 'mortality', farmId, farms })
  const { location } = crewLocation
  const productionUnitId = location?.productionUnitId ?? ''
  const selectedLotId = location?.lotId ?? ''

  const [rows, setRows] = useState<CauseRow[]>(() => [newRow()])
  const [entryDay, setEntryDay] = useState<CrewEntryDay>(0)
  const [note, setNote] = useState('')
  const [noteOpen, setNoteOpen] = useState(false)
  const [notCheckedOpen, setNotCheckedOpen] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [largeValuePending, setLargeValuePending] = useState(false)
  const [duplicateWarning, setDuplicateWarning] = useState<MortalityLog | null>(null)
  const [existingLogsForLot, setExistingLogsForLot] = useState<MortalityLog[]>([])
  const [lastVisit, setLastVisit] = useState<LastVisitStatus | null>(null)
  const firstCountRef = useRef<HTMLInputElement | null>(null)
  const focusRowId = useRef<string | null>(null)
  const countRefs = useRef(new Map<string, HTMLInputElement>())

  useEffect(() => {
    if (!farmId) return
    recordCrewEvent('capture.start', { stream: 'mortality', farmId })
  }, [farmId])

  useEffect(() => {
    if (!selectedLotId) {
      setExistingLogsForLot([])
      return
    }
    let cancelled = false
    void mortalityLogApi.list({ farmId, lotId: selectedLotId }, { preferLocal: true }).then((logs) => {
      if (cancelled) return
      setExistingLogsForLot(logs)
    })
    return () => {
      cancelled = true
    }
  }, [farmId, selectedLotId])

  // A new tank starts a new visit; the count opens focused (U-2, #51).
  useEffect(() => {
    setRows([newRow()])
    setNote('')
    setNoteOpen(false)
    setNotCheckedOpen(false)
    setEntryDay(0)
    resetConfirmationState()
    if (selectedLotId) requestAnimationFrame(() => firstCountRef.current?.focus())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLotId])

  useEffect(() => {
    const id = focusRowId.current
    if (!id) return
    focusRowId.current = null
    requestAnimationFrame(() => countRefs.current.get(id)?.focus())
  }, [rows])

  function resetConfirmationState() {
    setValidationError(null)
    setLargeValuePending(false)
    setDuplicateWarning(null)
  }

  const updateRow = (id: string, patch: Partial<CauseRow>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)))
    resetConfirmationState()
  }

  const addCauseRow = () => {
    const row = newRow()
    focusRowId.current = row.id
    setRows((prev) => [...prev, row])
  }

  const removeCauseRow = (id: string) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((row) => row.id !== id) : prev))
  }

  const describeVisit = (visit: MortalityVisit): string => {
    if (visit.kind === 'none') return t('crewMode.mortality.summary.none')
    if (visit.kind === 'notChecked') {
      return t('crewMode.mortality.summary.notChecked', { reason: t(notCheckedLabelKey[visit.reasonCode]) })
    }
    return visit.rows
      .map((row) => (row.causeCode ? `${row.count} · ${t(causeLabelKey[row.causeCode])}` : String(row.count)))
      .join(', ')
  }

  const saveVisit = async (visit: MortalityVisit) => {
    if (!farmId || !productionUnitId || !selectedLotId || !location) return
    const recordedAtIso = recordedAtForEntryDay(entryDay, new Date(), farmTimeZone(farmId))
    const payloads = buildMortalityVisitPayloads({
      farmId,
      productionUnitId,
      lotId: selectedLotId,
      lot: location.lotKey,
      recordedAtIso,
      capturedByActorId: getCurrentOperatorActorId(),
      visitId: createId('visit'),
      lateEntry: entryDay > 0,
      note,
      visit
    })

    const results: MortalityLogMutationResult[] = []
    for (const payload of payloads) {
      // Sequential: one outbox entry per cause row.
      // eslint-disable-next-line no-await-in-loop
      results.push(await mortalityLogApi.create(payload, { source: 'crewMode' }))
    }
    setExistingLogsForLot((prev) => [...prev, ...results.map((result) => result.data)])

    // Findings #88: the row of the entry's own farm day, also for a late entry.
    const entryDayKey = farmDayKey(recordedAtIso, farmTimeZone(farmId)) ?? undefined
    void ensureTodayProductionRow({ farmId, productionUnitId }, { id: selectedLotId, lotKey: location.lotKey ?? '' }, entryDayKey).catch(() => {})

    const ids = results.map((result) => result.data.id)
    const initialState = toSyncBadgeState(results[0]?.syncState ?? 'offline')
    recordCrewEvent('capture.confirm', {
      stream: 'mortality',
      farmId,
      syncState: initialState,
      capturedByActorId: getCurrentOperatorActorId()
    })

    setLastVisit({
      mortalityLogIds: ids,
      summary: describeVisit(visit),
      locationLabel: location.label,
      lateEntry: entryDay > 0,
      state: initialState
    })

    setRows([newRow()])
    setNote('')
    setNoteOpen(false)
    setNotCheckedOpen(false)
    setEntryDay(0)
    resetConfirmationState()

    void Promise.all(results.map((result) => result.syncPromise ?? Promise.resolve(null)))
      .then(async () => {
        const status = await mortalityLogApi.readMortalityLogSyncStatus(ids)
        if (status.blockedCount > 0) recordCrewEvent('capture.error', { stream: 'mortality', farmId })
        setLastVisit((prev) => (prev && prev.mortalityLogIds[0] === ids[0]
          ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : 'pending') }
          : prev))
      })
      .catch(() => {
        recordCrewEvent('capture.error', { stream: 'mortality', farmId })
        setLastVisit((prev) => (prev && prev.mortalityLogIds[0] === ids[0] ? { ...prev, state: 'error' } : prev))
      })
  }

  const handleConfirm = async () => {
    if (!selectedLotId) return
    const parsed: Array<{ causeCode: MortalityCauseCode | null; count: number }> = []
    for (const row of rows) {
      const validation = validateMortalityCountValue(row.count)
      if (!validation.ok) {
        setValidationError(t('crewMode.mortality.error.invalidValue'))
        return
      }
      parsed.push({ causeCode: row.causeCode, count: validation.value })
    }
    setValidationError(null)

    const total = parsed.reduce((sum, row) => sum + row.count, 0)
    const unusuallyLarge = location?.hatchery ? total > HATCHERY_COUNT_LARGE_WARNING_THRESHOLD : isUnusuallyLargeMortalityCount(total)
    if (!largeValuePending && unusuallyLarge) {
      setLargeValuePending(true)
      return
    }

    if (entryDay === 0) {
      const duplicate = findLikelyDuplicateMortalityLog({
        lotId: selectedLotId,
        recordedAtIso: new Date().toISOString(),
        existingLogs: existingLogsForLot,
        causeCodes: rows.map((row) => row.causeCode)
      })
      if (duplicate && duplicateWarning?.id !== duplicate.id) {
        setDuplicateWarning(duplicate)
        return
      }
    }

    await saveVisit({ kind: 'deaths', rows: parsed })
  }

  const handleUndo = async () => {
    if (!lastVisit) return
    const audit = buildOperatorUndoAudit(t('crewCorrection.reason.operatorUndo'))
    for (const id of lastVisit.mortalityLogIds) {
      // eslint-disable-next-line no-await-in-loop
      await mortalityLogApi.delete(id, audit)
    }
    const removed = new Set(lastVisit.mortalityLogIds)
    setExistingLogsForLot((prev) => prev.filter((log) => !removed.has(log.id)))
    setLastVisit(null)
    recordCrewEvent('capture.undo', { stream: 'mortality', farmId })
  }

  const handleRetry = async () => {
    if (!lastVisit) return
    await mortalityLogApi.retryMortalityLogSyncStatusWithReport(lastVisit.mortalityLogIds)
    const status = await mortalityLogApi.readMortalityLogSyncStatus(lastVisit.mortalityLogIds)
    setLastVisit((prev) => (prev
      ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : 'pending') }
      : prev))
  }

  // Findings #137: flip the badge once the background flush sends it.
  useRecheckWhileUnsynced(isUnsyncedBadgeState(lastVisit?.state), async () => {
    if (!lastVisit) return
    const ids = lastVisit.mortalityLogIds
    const status = await mortalityLogApi.readMortalityLogSyncStatus(ids)
    setLastVisit((prev) => (prev && prev.mortalityLogIds[0] === ids[0]
      ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : prev.state) }
      : prev))
  })

  if (farmId && !isMortalityCrewModeEnabled(farmId)) {
    return (
      <div style={{ maxWidth: 420, margin: '0 auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <CrewModeFarmBar farmId={farmId} farms={farms} onSelectFarm={selectFarm} />
        <div data-testid="mortality-capture-flag-disabled" style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>
          {t('crewMode.mortality.notEnabledForFarm')}
        </div>
      </div>
    )
  }

  const zeroLabel = entryDay === 0 ? t('crewMode.mortality.noneToday') : t('crewMode.mortality.noneThatDay')

  return (
    <OperatorGate>
      <div style={{ maxWidth: 420, margin: '0 auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <CrewModeFarmBar farmId={farmId} farms={farms} onSelectFarm={selectFarm} />

        <CrewLocationHeader stream="mortality" location={location} onChange={crewLocation.openPicker} />
        <CrewLocationPicker
          stream="mortality"
          open={crewLocation.pickerOpen}
          options={crewLocation.options}
          currentKey={location?.key}
          onChoose={(option) => {
            crewLocation.choose(option)
            setLastVisit(null)
          }}
          onClose={crewLocation.closePicker}
        />

        {selectedLotId && (
          <div role="group" aria-label={t('crewMode.day.label')} style={{ display: 'flex', gap: 8 }}>
            {DAY_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                data-testid={`mortality-capture-day-${option.value}`}
                aria-pressed={entryDay === option.value}
                onClick={() => setEntryDay(option.value)}
                style={{
                  ...chipButtonStyle,
                  flex: 1,
                  borderColor: entryDay === option.value ? '#1d4ed8' : '#d1d5db',
                  backgroundColor: entryDay === option.value ? '#eff6ff' : 'white'
                }}
              >
                {t(option.labelKey)}
              </button>
            ))}
          </div>
        )}

        {selectedLotId && (
          <button
            type="button"
            data-testid="mortality-capture-none"
            onClick={() => void saveVisit({ kind: 'none' })}
            style={zeroButtonStyle}
          >
            {zeroLabel}
          </button>
        )}

        {selectedLotId && rows.map((row, index) => (
          <div key={row.id} data-testid="mortality-capture-cause-row" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{t(location?.hatchery ? 'crewMode.mortality.countHatchery' : 'crewMode.mortality.count')}</label>
              {rows.length > 1 && (
                <button
                  type="button"
                  data-testid="mortality-capture-remove-cause"
                  onClick={() => removeCauseRow(row.id)}
                  style={{ ...chipButtonStyle, minHeight: 44, fontSize: 12 }}
                >
                  {t('crewMode.mortality.removeCause')}
                </button>
              )}
            </div>
            <input
              ref={(element) => {
                if (element) countRefs.current.set(row.id, element)
                else countRefs.current.delete(row.id)
                if (index === 0) firstCountRef.current = element
              }}
              data-testid="mortality-capture-count-input"
              type="text"
              inputMode="numeric"
              enterKeyHint="done"
              value={row.count}
              onChange={(event) => updateRow(row.id, { count: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void handleConfirm()
                }
              }}
              style={{
                width: '100%',
                minHeight: 56,
                padding: '8px 14px',
                borderRadius: 10,
                border: validationError ? '2px solid #dc2626' : '1px solid #d1d5db',
                fontSize: 24,
                fontWeight: 700
              }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(location?.hatchery ? HATCHERY_CAUSE_CODES : GROW_OUT_CAUSE_CODES).map((code) => (
                <button
                  key={code}
                  type="button"
                  data-testid={`mortality-capture-cause-${code}`}
                  aria-pressed={row.causeCode === code}
                  onClick={() => updateRow(row.id, { causeCode: row.causeCode === code ? null : code })}
                  style={{
                    ...chipButtonStyle,
                    borderColor: row.causeCode === code ? '#1d4ed8' : '#d1d5db',
                    backgroundColor: row.causeCode === code ? '#eff6ff' : 'white'
                  }}
                >
                  {t(causeLabelKey[code])}
                </button>
              ))}
            </div>
          </div>
        ))}

        {selectedLotId && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" data-testid="mortality-capture-add-cause" onClick={addCauseRow} style={{ ...secondaryButtonStyle, flex: 1 }}>
              {t('crewMode.mortality.addCause')}
            </button>
            <button type="button" data-testid="mortality-capture-note-toggle" onClick={() => setNoteOpen((open) => !open)} style={{ ...secondaryButtonStyle, flex: 1 }}>
              {t('crewMode.mortality.note')}
            </button>
          </div>
        )}

        {selectedLotId && noteOpen && (
          <textarea
            data-testid="mortality-capture-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            placeholder={t('crewMode.mortality.notePlaceholder')}
            style={{ width: '100%', minHeight: 56, padding: 10, borderRadius: 10, border: '1px solid #d1d5db', fontSize: 16 }}
          />
        )}

        {validationError && (
          <div data-testid="mortality-capture-validation-error" style={{ color: '#dc2626', fontWeight: 700 }}>{validationError}</div>
        )}
        {largeValuePending && (
          <div data-testid="mortality-capture-large-value-warning" style={{ color: '#92400e', fontWeight: 700 }}>
            {t('crewMode.mortality.warning.largeValue')}
          </div>
        )}
        {duplicateWarning && (
          <div data-testid="mortality-capture-duplicate-warning" style={{ color: '#92400e', fontWeight: 700 }}>
            {t('crewMode.mortality.warning.duplicate')}
          </div>
        )}

        {selectedLotId && (
          <button type="button" data-testid="mortality-capture-confirm" onClick={() => void handleConfirm()} style={primaryButtonStyle}>
            {largeValuePending || duplicateWarning ? t('crewMode.mortality.confirmAnyway') : t('crewMode.mortality.confirm')}
          </button>
        )}

        {selectedLotId && (
          notCheckedOpen ? (
            <div data-testid="mortality-capture-not-checked" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 12, border: '1px dashed #9ca3af' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{t('crewMode.mortality.notChecked.prompt')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {NOT_CHECKED_REASON_CODES.map((code) => (
                  <button
                    key={code}
                    type="button"
                    data-testid={`mortality-capture-not-checked-${code}`}
                    onClick={() => void saveVisit({ kind: 'notChecked', reasonCode: code })}
                    style={chipButtonStyle}
                  >
                    {t(notCheckedLabelKey[code])}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setNotCheckedOpen(false)} style={secondaryButtonStyle}>
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="mortality-capture-not-checked-open"
              onClick={() => setNotCheckedOpen(true)}
              style={{ ...secondaryButtonStyle, borderStyle: 'dashed' }}
            >
              {t('crewMode.mortality.notChecked.open')}
            </button>
          )
        )}

        {lastVisit && (
          <div
            data-testid="mortality-capture-last-entry"
            style={{ padding: 12, borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, ...syncBadgeStyle[lastVisit.state] }}
          >
            <span style={{ fontWeight: 700, minWidth: 0 }}>
              <span data-testid="mortality-capture-last-location" style={{ display: 'block', fontSize: 13 }}>
                {lastVisit.locationLabel}
                {lastVisit.lateEntry ? ` · ${t('crewMode.lateEntry')}` : ''}
              </span>
              {lastVisit.summary}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" data-testid="mortality-capture-undo" onClick={() => void handleUndo()} style={{ ...chipButtonStyle, minHeight: 44, fontSize: 13 }}>
                {t('crewMode.undo')}
              </button>
              <span data-testid="mortality-capture-sync-state" style={{ fontWeight: 800 }}>{t(syncBadgeLabelKey[lastVisit.state])}</span>
              {lastVisit.state === 'error' && (
                <button type="button" data-testid="mortality-capture-retry" onClick={() => void handleRetry()} style={{ ...chipButtonStyle, minHeight: 44, fontSize: 12 }}>
                  {t('crewMode.mortality.retry')}
                </button>
              )}
            </span>
          </div>
        )}

        {lastVisit && (
          <CrewNextLocationButton
            next={crewLocation.next}
            onGo={(option) => {
              crewLocation.choose(option)
              setLastVisit(null)
            }}
          />
        )}
      </div>
    </OperatorGate>
  )
}
